import { appKey } from '@app/shared/brand'
import { Moon, Sun } from 'lucide-react'
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { Button } from './index'
import { Tooltip } from './Tooltip'

type Theme = 'light' | 'dark' | 'system'

const KEY = appKey('theme')

function storedTheme(): Theme {
  const raw = localStorage.getItem(KEY)
  if (raw === 'light' || raw === 'dark' || raw === 'system') return raw
  return 'system'
}

function applyTheme(theme: Theme) {
  const root = document.documentElement
  if (theme === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', theme)
  localStorage.setItem(KEY, theme)
}

const ThemeContext = createContext<{
  theme: Theme
  setTheme: (theme: Theme) => void
} | null>(null)

/**
 * Theme control. Defaults to `system`, so the app follows the OS unless the
 * person overrides it — the override is written to the root element as
 * `data-theme`, which the token layer treats as the winning signal in both
 * directions.
 *
 * One source, above every routed surface, so a screen that stands outside the
 * shells still sees the saved preference.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const initial = storedTheme()
    applyTheme(initial)
    return initial
  })

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}

/**
 * The reader's own light or dark setting.
 *
 * `labeled` sets the word beside the icon. It is what a footer needs: there the
 * icon stands beside the platform line with no neighbor of its own kind, and a
 * moon read as part of "Powered by" offers to put the product to sleep, and a
 * touch has no hover to name an icon. Unlabeled, it sits in a row of controls
 * that explain one another, and a tooltip names it. Either way its name is
 * the act, so a screen reader hears what pressing it does rather than a
 * state.
 */
export function ThemeToggle({ labeled = false }: { labeled?: boolean }) {
  const { theme, setTheme } = useTheme()
  const prefersDark =
    typeof matchMedia === 'function' &&
    matchMedia('(prefers-color-scheme: dark)').matches
  const isDark = theme === 'dark' || (theme === 'system' && prefersDark)
  const next = isDark ? 'Light theme' : 'Dark theme'
  const glyph = isDark ? (
    <Sun className="btn__icon" aria-hidden />
  ) : (
    <Moon className="btn__icon" aria-hidden />
  )
  const flip = () => setTheme(isDark ? 'light' : 'dark')

  if (labeled) {
    return (
      <Button
        variant="quiet"
        aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
        onClick={flip}
      >
        {glyph}
        {next}
      </Button>
    )
  }
  return (
    <Tooltip label={next}>
      <Button
        icon
        variant="quiet"
        aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
        onClick={flip}
      >
        {glyph}
      </Button>
    </Tooltip>
  )
}
